import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { auth } from '../../services/firebase';
import {
	sendPasswordResetEmail,
	verifyPasswordResetCode,
	confirmPasswordReset,
	signInWithEmailAndPassword
} from 'firebase/auth';
import HeadersNonAuth from '../../components/base/HeadersNonAuth';

export default function ForgotPassword() {
	const navigate = useNavigate();
	const location = useLocation();

	const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const mode = query.get('mode');
	const oobCode = query.get('oobCode');

	const [email, setEmail] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [message, setMessage] = useState('');
	const [error, setError] = useState('');
	const [verifiedEmail, setVerifiedEmail] = useState('');

	// If coming from Firebase reset link, verify code first
	useEffect(() => {
		let cancelled = false;
		async function verifyCode() {
			if (mode === 'resetPassword' && oobCode) {
				try {
					const emailFromCode = await verifyPasswordResetCode(auth, oobCode);
					if (!cancelled) {
						setVerifiedEmail(emailFromCode);
						setMessage(`Resetting password for ${emailFromCode}`);
						setError('');
					}
				} catch (e) {
					if (!cancelled) {
						setError('This password reset link is invalid or has expired. Please request a new one.');
					}
				}
			}
		}
		verifyCode();
		return () => {
			cancelled = true;
		};
	}, [mode, oobCode]);

	// Check if user just completed password reset (no oobCode but has mode=resetPassword)
	useEffect(() => {
		console.log('🔍 Checking redirect conditions:', { mode, oobCode, hasMode: !!mode, hasOobCode: !!oobCode });
		
		if (mode === 'resetPassword' && !oobCode) {
			console.log('🔄 Password reset completed, redirecting to login...');
			// User just completed password reset, redirect to login immediately
			navigate('/login', { replace: true });
		}
	}, [mode, oobCode, navigate]);

	// More aggressive redirect check - runs on every render
	useEffect(() => {
		const checkAndRedirect = () => {
			const urlParams = new URLSearchParams(window.location.search);
			const urlMode = urlParams.get('mode');
			const urlOobCode = urlParams.get('oobCode');
			
			console.log('🔍 Aggressive redirect check:', { urlMode, urlOobCode, pathname: window.location.pathname });
			
			// If we're on forgot-password page and password reset was completed, redirect
			if (window.location.pathname === '/forgot-password') {
				if (urlMode === 'resetPassword' && !urlOobCode) {
					console.log('🔄 Password reset completed detected, redirecting to login...');
					navigate('/login', { replace: true });
					return;
				}
				
				// Check sessionStorage flag
				const resetCompleted = sessionStorage.getItem('passwordResetCompleted');
				if (resetCompleted) {
					console.log('🔄 Password reset flag found, redirecting to login...');
					sessionStorage.removeItem('passwordResetCompleted');
					navigate('/login', { replace: true });
					return;
				}
			}
		};
		
		// Check immediately
		checkAndRedirect();
		
		// Also check after a short delay
		const timer = setTimeout(checkAndRedirect, 1000);
		
		return () => clearTimeout(timer);
	}, [navigate]);

	const handleSendReset = async (e) => {
		e.preventDefault();
		setSubmitting(true);
		setError('');
		setMessage('');
		try {
			// Check if user exists in Firestore first
			const { getFirestore, collection, query, where, getDocs } = await import('firebase/firestore');
			const db = getFirestore();
			const usersRef = collection(db, 'users');
			const q = query(usersRef, where('email', '==', email.trim().toLowerCase()));
			const querySnapshot = await getDocs(q);
			
			if (querySnapshot.empty) {
				setError('No account found with this email address. Please check your email or sign up for a new account.');
				return;
			}
			
			// User exists in Firestore, try to send reset email
			// If it fails, we'll handle the error appropriately
			console.log('📧 Attempting to send password reset email to:', email);
			console.log('🔗 Using URL:', 'https://qfaa.jtncgroup.com/login');
			
			// Enhanced ActionCodeSettings to prevent junk mail
			await sendPasswordResetEmail(auth, email.trim().toLowerCase(), {
				url: 'https://qfaa.jtncgroup.com/login',
				handleCodeInApp: true,
				// Additional settings to improve email delivery
				android: {
					packageName: 'com.jtncgroup.quantumflo',
					installApp: false,
					minimumVersion: '12'
				},
				// iOS settings (if applicable)
				iOS: {
					bundleId: 'com.jtncgroup.quantumflo'
				},
				// Dynamic link domain (if you have one)
				// dynamicLinkDomain: 'your-domain.page.link'
			});
			
			console.log('✅ Password reset email sent successfully');
			setMessage('We sent a password reset email. Please check your inbox (and spam/junk folder if not found).');
		} catch (e) {
			console.error('Password reset error:', e);
			if (e.code === 'auth/user-not-found') {
				// User exists in Firestore but not in Firebase Auth
				// This means they signed up through the API but not through Firebase Auth
				setError('Account found but not properly set up. Please contact support or try signing up again.');
			} else if (e.code === 'auth/invalid-email') {
				setError('Please enter a valid email address.');
			} else if (e.code === 'auth/too-many-requests') {
				setError('Too many reset attempts. Please wait a few minutes before trying again.');
			} else {
				setError('Failed to send reset email. Please try again or contact support.');
			}
		} finally {
			setSubmitting(false);
		}
	};

	const handleConfirmReset = async (e) => {
		e.preventDefault();
		if (newPassword.length < 6) {
			setError('Password must be at least 6 characters.');
			return;
		}
		if (newPassword !== confirmPassword) {
			setError('Passwords do not match.');
			return;
		}
		setSubmitting(true);
		setError('');
		setMessage('');
		try {
			// Reset password using Firebase Auth
			await confirmPasswordReset(auth, oobCode, newPassword);
			
			// Update users collection with password change info
			const { getFirestore, doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
			const db = getFirestore();
			
			// Find user document by email
			const { collection, query, where, getDocs } = await import('firebase/firestore');
			const usersRef = collection(db, 'users');
			const q = query(usersRef, where('email', '==', verifiedEmail));
			const querySnapshot = await getDocs(q);
			
			if (!querySnapshot.empty) {
				const userDoc = querySnapshot.docs[0];
				await updateDoc(doc(db, 'users', userDoc.id), {
					passwordUpdatedAt: serverTimestamp(),
					lastPasswordChange: serverTimestamp(),
					passwordResetViaEmail: true,
					passwordResetDate: serverTimestamp()
				});
				console.log('✅ Password change logged to users collection for:', verifiedEmail);
			}
			
			setMessage('Password has been reset successfully. Redirecting to login...');
			
			// Set flag in sessionStorage to indicate password reset was completed
			sessionStorage.setItem('passwordResetCompleted', 'true');
			
			// Redirect to login immediately after successful password reset
			setTimeout(() => {
				navigate('/login');
			}, 1500); // Wait 1.5 seconds to show success message
		} catch (e) {
			console.error('Password reset error:', e);
			setError(e?.message || 'Failed to reset password. Try requesting a new link.');
		} finally {
			setSubmitting(false);
		}
	};

	const isResetMode = mode === 'resetPassword' && !!oobCode;


	return (
		<>
			<HeadersNonAuth />
			<div className="min-h-screen bg-gray-950 flex items-center justify-center px-4 pt-24 pb-16">
				<div className="w-full max-w-md bg-gray-900 border border-gray-800 rounded-xl p-6">
					{/* Show completion message if password reset was just completed */}
					{mode === 'resetPassword' && !oobCode ? (
						<>
							<h1 className="text-white text-2xl font-semibold mb-1">Password Reset Complete</h1>
							<p className="text-gray-400 text-sm mb-6">Your password has been successfully reset.</p>
							<div className="text-center">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-4"></div>
								<p className="text-green-400 text-sm mb-4">Redirecting to login page...</p>
								<button 
									onClick={() => navigate('/login', { replace: true })}
									className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-medium"
								>
									Go to Login
								</button>
							</div>
						</>
					) : (
						<>
							<h1 className="text-white text-2xl font-semibold mb-1">{isResetMode ? 'Set a new password' : 'Forgot password'}</h1>
							<p className="text-gray-400 text-sm mb-6">{isResetMode ? 'Please enter your new password below.' : 'Enter your email and we will send you a reset link.'}</p>

							{message && <div className="mb-4 text-sm text-green-400">{message}</div>}
							{error && <div className="mb-4 text-sm text-red-400">{error}</div>}

							{isResetMode ? (
								<form onSubmit={handleConfirmReset} className="space-y-4">
									{verifiedEmail && (
										<div className="text-sm text-gray-300">Reset for: <span className="font-medium">{verifiedEmail}</span></div>
									)}
									<div>
										<label className="block text-gray-300 text-sm mb-1">New Password</label>
										<div className="relative">
											<input 
												type={showNewPassword ? "text" : "password"} 
												className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 pr-10 border border-gray-700 focus:border-purple-500 focus:outline-none"
												value={newPassword} 
												onChange={(e) => setNewPassword(e.target.value)} 
												placeholder="Your new password" 
											/>
											<button
												type="button"
												className="absolute inset-y-0 right-0 pr-3 flex items-center"
												onClick={() => setShowNewPassword(!showNewPassword)}
											>
												{showNewPassword ? (
													<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
													</svg>
												) : (
													<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
													</svg>
												)}
											</button>
										</div>
									</div>
									<div>
										<label className="block text-gray-300 text-sm mb-1">Confirm Password</label>
										<div className="relative">
											<input 
												type={showConfirmPassword ? "text" : "password"} 
												className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 pr-10 border border-gray-700 focus:border-purple-500 focus:outline-none"
												value={confirmPassword} 
												onChange={(e) => setConfirmPassword(e.target.value)} 
												placeholder="Confirm new password" 
											/>
											<button
												type="button"
												className="absolute inset-y-0 right-0 pr-3 flex items-center"
												onClick={() => setShowConfirmPassword(!showConfirmPassword)}
											>
												{showConfirmPassword ? (
													<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
													</svg>
												) : (
													<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
													</svg>
												)}
											</button>
										</div>
									</div>
									<button disabled={submitting} className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-500 disabled:opacity-60 text-white rounded-lg font-medium">
										{submitting ? 'Updating...' : 'Update Password'}
									</button>
									<div className="text-center mt-3 text-sm">
										<Link to="/login" className="text-purple-400 hover:text-purple-300">Back to login</Link>
									</div>
								</form>
							) : (
								<>
									<div className="border-t border-gray-800 pt-6">
										<h2 className="text-white font-medium mb-2">Send reset link</h2>
										<form className="space-y-4" onSubmit={handleSendReset}>
											<div>
												<label className="block text-gray-300 text-sm mb-1">Email address</label>
												<input 
													type="email" 
													className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:border-purple-500 focus:outline-none"
													value={email} 
													onChange={(e) => setEmail(e.target.value)} 
													placeholder="you@example.com" 
												/>
											</div>
											<button disabled={submitting} className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-500 disabled:opacity-60 text-white rounded-lg font-medium">
												{submitting ? 'Sending...' : 'Send reset link'}
											</button>
											<div className="text-center mt-3 text-sm">
												<Link to="/login" className="text-purple-400 hover:text-purple-300">Back to login</Link>
											</div>
										</form>
									</div>
								</>
							)}
						</>
					)}
				</div>
			</div>
		</>
	);
} 